export interface IBooleanQueryObject {
  id: number;
  baseOperator: 'ROOT' | 'AND' | 'OR' | 'NOT';
  operator: 'AND' | 'OR';
  queryValues: Array<string>;
}

function detectOperator(group: string): string {
  if (group.includes('AND')) {
    return 'AND';
  }
  return 'OR'; // Default to "OR" if "AND" is not found
}

export const booleanQueryToEditableArray = (booleanQuery: string) => {
  // Step 1: Define the base operators to look for
  const baseOperatorsRegex = /\b(AND|OR|NOT)\b/g;
  const parenthesisContentRegex = /(?<=\().*?(?=\))/g;

  // Step 2: Match the query values inside parentheses
  const matches = [...(booleanQuery.match(parenthesisContentRegex) || [])];

  if (matches.length === 0) {
    throw new Error('No valid groups found in the booleanQuery string');
  }

  // Step 3: Find all the base operators between the groups
  const baseOperators: IBooleanQueryObject['baseOperator'][] = [];

  // Step 4: The first group is always assigned the 'ROOT' operator
  baseOperators.push('ROOT');

  const onlyBaseOperators = booleanQuery.replace(parenthesisContentRegex, '');
  let match = baseOperatorsRegex.exec(onlyBaseOperators);

  while (match !== null) {
    baseOperators.push(match[0] as IBooleanQueryObject['baseOperator']);
    match = baseOperatorsRegex.exec(onlyBaseOperators);
  }

  // Step 5: Build the array of objects
  return matches.map((match, index) => {
    const group = match.trim();
    const operator = detectOperator(group);

    // Extract query values while keeping the operator intact between values
    const queryValues = group
      .split(new RegExp(`\\b${operator}\\b`, 'g'))
      .map((value) => value.trim());

    return {
      id: index,
      baseOperator: baseOperators[index].trim() as IBooleanQueryObject['baseOperator'],
      operator: operator as IBooleanQueryObject['operator'],
      queryValues
    };
  });
};

export const editableBooleanArrayToBooleanQuery = (
  editableBooleanArray: Array<IBooleanQueryObject>
) => {
  return editableBooleanArray
    .map((obj, index) => {
      // Join queryValues using the provided operator ("AND" or "OR")
      const queryPart = `(${obj.queryValues.join(` ${obj.operator} `)})`;

      // For the first element, we don't prepend the baseOperator
      if (index === 0) {
        return queryPart;
      }

      // Prepend the baseOperator before the query part
      return `${obj.baseOperator} ${queryPart}`;
    })
    .join(' ');
};
